import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/GlobalStyles.css'
import { Building, MapPin, Clock, IndianRupee, Briefcase, GraduationCap, ArrowLeft, CheckCircle } from 'lucide-react';

const JobDescription = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [applying, setApplying] = useState(false);
    const [institution, setInstitution] = useState(null);
    const [candidateProfile, setCandidateProfile] = useState(null);
    const [isApplied, setIsApplied] = useState(false);

    // Cover letter modal state
    const [showCoverModal, setShowCoverModal] = useState(false);
    const [coverLetter, setCoverLetter] = useState('');

    // Fetch candidate profile to check verification status
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/profile/getCandProfile`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setCandidateProfile(res.data.candidate);
            } catch (err) {
                console.error("Failed to load profile for verification check", err);
            }
        };
        fetchProfile();
    }, []);

    // Check applied status from backend
    useEffect(() => {
        const checkAppliedStatus = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token || !id) return;

                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/job/apply/allAppliedJobs`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const appliedIds = res.data.map(app => {
                    const jId = app.job_id;
                    if (!jId) return null;
                    if (typeof jId === 'object' && jId._id) return jId._id.toString();
                    return jId.toString();
                }).filter(Boolean);

                setIsApplied(appliedIds.includes(id.toString()));
            } catch (err) {
                console.error("Failed to check applied status", err);
            }
        };

        checkAppliedStatus();
    }, [id]);

    // Fetch job details
    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/getJob/${id}`);
                setJob(response.data.data);
            } catch (err) {
                console.error("Error fetching job details:", err);
                setError(`Failed to load job details. ${err.response?.statusText || err.message}`);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchJobDetails();
    }, [id]);

    // Fetch institution details
    useEffect(() => {
        if (job?.Institution_id?._id) {
            const fetchInstitution = async () => {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/getPublicInst/${job.Institution_id._id}`);
                    setInstitution(response.data.data);
                } catch (err) {
                    console.error("Error fetching institution details:", err);
                }
            };
            fetchInstitution();
        }
    }, [job]);

    // Check if job is closed/expired
    const isJobClosed = () => {
        if (!job) return false;
        if (job.status === 'closed' || job.is_active === false) return true;
        if (job.closing_date && new Date(job.closing_date) < new Date()) return true;
        return false;
    };

    // Open cover letter modal (or redirect to login/show verification toast)
    const handleApplyClick = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.info("Please login to apply for this job.");
            navigate('/login');
            return;
        }

        if (candidateProfile && !candidateProfile.admin_verified) {
            toast.warning("Your account is pending verification. Please wait until you are verified.");
            return;
        }

        setShowCoverModal(true);
    };

    // Submit the actual application
    const handleSubmitApplication = async () => {
        setShowCoverModal(false);
        setApplying(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/job/apply/${id}`,
                {
                    job_title: job.title,
                    cover: coverLetter.trim() || undefined,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            toast.success(response.data.message || "Application submitted successfully!");
            setIsApplied(true);
            setCoverLetter('');

        } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.message?.includes("incomplete")) {
                toast.error("Your profile is incomplete. Please complete your Personal Profile and Work Preferences.", {
                    autoClose: 6000,
                    onClick: () => navigate('/ProfilePage'),
                });
            } else if (error.response?.status === 409) {
                toast.info("You have already applied for this job.");
                setIsApplied(true);
            } else {
                toast.error(error.response?.data?.message || 'Failed to apply. Please try again.');
            }
        } finally {
            setApplying(false);
        }
    };

    if (loading) return <div className="py-20 text-center">Loading job details...</div>;
    if (error) return <div className="py-20 text-center text-red-500">{error}</div>;
    if (!job) return <div className="py-20 text-center">Job not found.</div>;

    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
    const jobClosed = isJobClosed();

    return (
        <div className="global-font flex flex-col min-h-screen bg-gray-50 pt-16">
            <Navbar />
            <main className="flex-grow container px-4 py-8 mx-auto max-w-5xl">
                <button onClick={() => navigate(-1)} className="flex items-center text-blue-600 hover:text-blue-800 mb-6">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Jobs
                </button>

                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="p-6 md:p-8 border-b">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800 mb-2">{job.title}</h1>
                                <div className="flex items-center text-gray-600 mb-4">
                                    <Building className="w-5 h-5 mr-2" />
                                    <span className="font-semibold text-lg">{job.Institution_id?.name || "Unknown Institution"}</span>
                                </div>
                                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                    <div className="flex items-center">
                                        <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                                        {job.address?.area},{job.address?.city}, {job.address?.state}
                                    </div>
                                    <div className="flex items-center">
                                        <Briefcase className="w-4 h-4 mr-1 text-gray-400" />
                                        {job.employment_type?.replace('_', ' ')}
                                    </div>
                                    <div className="flex items-center">
                                        <Clock className="w-4 h-4 mr-1 text-gray-400" />
                                        {job.min_experience} - {job.max_experience} Years
                                    </div>
                                    {job.salary_range?.is_visible_to_applicants && (job.salary_range.lakh > 0 || job.salary_range.thousand > 0) && (
                                        <div className="flex items-center">
                                            <IndianRupee className="w-4 h-4 mr-1 text-gray-400" />
                                            {((job.salary_range.lakh || 0) * 100000 + (job.salary_range.thousand || 0) * 1000).toLocaleString('en-IN')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-6 md:mt-0">
                                {jobClosed ? (
                                    <button disabled className="flex items-center px-6 py-3 bg-red-100 text-red-700 font-semibold rounded-md border border-red-200 cursor-not-allowed">
                                        Applications Closed
                                    </button>
                                ) : isApplied ? (
                                    <button disabled className="flex items-center px-6 py-3 bg-green-100 text-green-700 font-semibold rounded-md border border-green-200 cursor-not-allowed">
                                        <CheckCircle className="w-5 h-5 mr-2" /> Applied
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleApplyClick}
                                        disabled={applying}
                                        className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition disabled:opacity-70"
                                    >
                                        {applying ? 'Applying...' : 'Apply Now'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 md:p-8 grid md:grid-cols-3 gap-8">
                        <div className="md:col-span-2 space-y-8">
                            <section>
                                <h2 className="text-xl font-bold text-gray-800 mb-4">Job Description</h2>
                                <p className="text-gray-600 whitespace-pre-line leading-relaxed">{job.description}</p>
                            </section>

                            {(job.requirements || (Array.isArray(job.requirements) && job.requirements.length > 0)) && (
                                <section>
                                    <h2 className="text-xl font-bold text-gray-800 mb-4">Requirements</h2>
                                    {Array.isArray(job.requirements) ? (
                                        <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                            {job.requirements.map((req, i) => <li key={i}>{req}</li>)}
                                        </ul>
                                    ) : (
                                        <p className="text-gray-600">{job.requirements}</p>
                                    )}
                                </section>
                            )}

                            {job.responsibilities && (
                                <section>
                                    <h2 className="text-xl font-bold text-gray-800 mb-4">Responsibilities</h2>
                                    {Array.isArray(job.responsibilities) ? (
                                        <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                            {job.responsibilities.map((req, i) => <li key={i}>{req}</li>)}
                                        </ul>
                                    ) : (
                                        <p className="text-gray-600">{job.responsibilities}</p>
                                    )}
                                </section>
                            )}

                            {job.benefits && (
                                <section>
                                    <h2 className="text-xl font-bold text-gray-800 mb-4">Benefits</h2>
                                    {Array.isArray(job.benefits) ? (
                                        <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                            {job.benefits.map((req, i) => <li key={i}>{req}</li>)}
                                        </ul>
                                    ) : (
                                        <p className="text-gray-600">{job.benefits}</p>
                                    )}
                                </section>
                            )}

                        </div>

                        <div className="md:col-span-1 space-y-6">
                            <div className="bg-gray-50 p-5 rounded-lg border border-gray-100">
                                <h3 className="font-semibold text-gray-800 mb-4">Job Overview</h3>
                                <div className="space-y-4">
                                    <div>
                                        <span className="text-sm text-gray-500 block">Date Posted</span>
                                        <span className="text-gray-700 font-medium">{formatDate(job.posted_date)}</span>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500 block">Department</span>
                                        <span className="text-gray-700 font-medium">{job.domain}</span>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500 block">Qualification</span>
                                        <div className="text-gray-700 font-medium mt-1">
                                            {Array.isArray(job.prefered_qualifications) && job.prefered_qualifications.length > 0 ? (
                                                <div className="flex flex-col gap-2">
                                                    {job.prefered_qualifications.map((qual, index) => (
                                                        <div key={index} className="flex items-start">
                                                            <GraduationCap className="w-4 h-4 mr-2 text-gray-400 mt-1 flex-shrink-0" />
                                                            <span>{qual}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="flex items-center">
                                                    <GraduationCap className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                                                    <span>{job.prefered_qualifications || "Not specified"}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-blue-50 p-5 rounded-lg border border-blue-100">
                                <h3 className="font-semibold text-blue-800 mb-2">About Institution</h3>
                                <p className="text-sm text-blue-900 mb-3">{institution?.description || job.Institution_id?.description || "No description available."}</p>
                                {job.Institution_id?.website && (
                                    <a
                                        href={job.Institution_id.website.startsWith('http') ? job.Institution_id.website : `http://${job.Institution_id.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 font-medium hover:underline"
                                    >
                                        Visit Website
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* ===== COVER LETTER MODAL ===== */}
            {showCoverModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Apply for {job.title}</h3>
                        <p className="text-sm text-gray-500 mb-4">Write a cover letter to introduce yourself to the institution. This is optional but recommended.</p>

                        <textarea
                            rows={6}
                            value={coverLetter}
                            onChange={(e) => setCoverLetter(e.target.value)}
                            placeholder="Tell the institution why you're a great fit for this role..."
                            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                        />

                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                onClick={() => setShowCoverModal(false)}
                                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitApplication}
                                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-semibold"
                            >
                                Submit Application
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default JobDescription;
